import { Box, Button, Stack, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useDeleteSsoClientMutation } from "Api/Mutations/SsoClients/useDeleteSsoClientMutation";
import { useRotateSsoClientSecretMutation } from "Api/Mutations/SsoClients/useRotateSsoClientSecretMutation";
import { useSsoClientsQuery } from "Api/Queries/SsoClients/useSsoClientsQuery";
import { ClientSecretDialog } from "Components/Admin/SsoClients/ClientSecretDialog";
import { SsoClientFormDialog } from "Components/Admin/SsoClients/SsoClientFormDialog";
import { SsoClientListTable } from "Components/Admin/SsoClients/SsoClientListTable";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import { ConfirmDialog } from "Components/Shared/Dialogs/ConfirmDialog";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { StyledPageWrapper } from "Components/Shared/StyledComponents";
import { useConfirmDialog } from "Hooks/useConfirmDialog";
import type { SsoClientDto } from "Infrastructure/Api/Api";
import { type ReactNode, useState } from "react";
import { Resources, useResource } from "Translations/Resources";
import { toast } from "sonner";

const RESOURCES = Resources.Admin.SsoClients;

type IssuedSecret = { clientId: string; secret: string };

export const SsoClientsPage = () => {
  const { t } = useResource();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [clientToEdit, setClientToEdit] = useState<SsoClientDto | null>(null);
  const [clientToDelete, setClientToDelete] = useState<SsoClientDto | null>(
    null,
  );
  const [clientToRotate, setClientToRotate] = useState<SsoClientDto | null>(
    null,
  );
  const [issuedSecret, setIssuedSecret] = useState<IssuedSecret | null>(null);

  const { data, isLoading, isError } = useSsoClientsQuery();

  const deleteDialog = useConfirmDialog();
  const rotateDialog = useConfirmDialog();

  const { mutate: deleteClient } = useDeleteSsoClientMutation({
    onSuccess: () => {
      toast.success(t(RESOURCES.Messages.DeleteSuccess));
      setClientToDelete(null);
    },
    onError: () => toast.error(t(RESOURCES.Messages.DeleteError)),
  });

  const { mutate: rotateSecret } = useRotateSsoClientSecretMutation({
    onSuccess: (result) => {
      setClientToRotate(null);
      setIssuedSecret({
        clientId: result.clientId,
        secret: result.clientSecret,
      });
    },
    onError: () => toast.error(t(RESOURCES.Messages.RotateError)),
  });

  if (!isLoading && (isError || !data)) {
    return (
      <Layout>
        <ErrorText>{t(RESOURCES.Messages.LoadError)}</ErrorText>
      </Layout>
    );
  }

  const handleDeleteRequest = (client: SsoClientDto) => {
    setClientToDelete(client);
    deleteDialog.open();
  };

  const handleRotateRequest = (client: SsoClientDto) => {
    setClientToRotate(client);
    rotateDialog.open();
  };

  return (
    <Layout>
      <Stack gap={4}>
        <Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsCreateDialogOpen(true)}
          >
            {t(RESOURCES.CreateButton)}
          </Button>
        </Box>
        <Box display="grid" alignItems="start">
          <SsoClientListTable
            data={data ?? []}
            isLoading={isLoading}
            onRowClick={setClientToEdit}
            onRotateSecret={handleRotateRequest}
            onDelete={handleDeleteRequest}
          />
        </Box>
      </Stack>
      {isCreateDialogOpen && (
        <SsoClientFormDialog
          isOpen
          onClose={() => setIsCreateDialogOpen(false)}
          onSecretIssued={(clientId, secret) =>
            setIssuedSecret({ clientId, secret })
          }
        />
      )}
      {clientToEdit && (
        <SsoClientFormDialog
          isOpen
          client={clientToEdit}
          onClose={() => setClientToEdit(null)}
          onSecretIssued={(clientId, secret) =>
            setIssuedSecret({ clientId, secret })
          }
        />
      )}
      {issuedSecret && (
        <ClientSecretDialog
          clientId={issuedSecret.clientId}
          secret={issuedSecret.secret}
          onClose={() => setIssuedSecret(null)}
        />
      )}
      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        handleResult={deleteDialog.handleResult(() => {
          if (clientToDelete) {
            deleteClient(clientToDelete.clientId);
          }
        })}
        modalTitle={t(RESOURCES.Messages.DeleteConfirmTitle)}
        modalContent={
          <Typography>
            {t(RESOURCES.Messages.DeleteConfirm, {
              clientId: clientToDelete?.clientId ?? "",
            })}
          </Typography>
        }
        isDestructive
      />
      <ConfirmDialog
        isOpen={rotateDialog.isOpen}
        handleResult={rotateDialog.handleResult(() => {
          if (clientToRotate) {
            rotateSecret(clientToRotate.clientId);
          }
        })}
        modalTitle={t(RESOURCES.Messages.RotateConfirmTitle)}
        modalContent={
          <Typography>
            {t(RESOURCES.Messages.RotateConfirm, {
              clientId: clientToRotate?.clientId ?? "",
            })}
          </Typography>
        }
        isDestructive
      />
    </Layout>
  );
};

const Layout = ({ children }: { children: ReactNode }) => {
  const { t } = useResource();

  return (
    <AuthenticatedLayout title={t(RESOURCES.PageTitle)}>
      <StyledPageWrapper maxWidth="unset !important">
        {children}
      </StyledPageWrapper>
    </AuthenticatedLayout>
  );
};

export default SsoClientsPage;
